import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SpecraButton, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { CircleHelp } from 'lucide-react';
import { useState } from 'react';
/**
 * What the chat can do, grouped by the job someone came to do. Written for a
 * first-time user: what to type or click, not feature names.
 */ export const CAPABILITIES = [
    {
        title: 'Ask about your work',
        how: 'Ask in plain words about programs, deliverables, pursuits, contracts and dates. Answers link to the records they came from.',
        example: 'What needs my attention this week?'
    },
    {
        title: 'Point at a record with @',
        how: 'Type @ and pick a program, deliverable, document, person or company. The assistant uses that exact record instead of guessing from a name.',
        example: "What's due next on @"
    },
    {
        title: 'Work with documents',
        how: 'Attach a file with the paperclip, pick one from Documents with the library button, or @-mention it. Ask for a summary, a comparison or the requirements in it.',
        example: 'Summarize @'
    },
    {
        title: 'Create documents',
        how: 'Ask for a Word, Excel or PowerPoint file, or start from a template in your Library. You review each change before it is applied.',
        example: 'What templates do we have?'
    },
    {
        title: 'Have it remember things',
        how: 'Start with "Remember that…", or use the brain button in a chat. Your notes carry into every chat; a note for the whole team waits for an admin to approve it.',
        example: 'Remember that I prefer short answers with bullet points'
    },
    {
        title: 'Short or thorough answers',
        how: 'Turn on Quick in a chat for a short answer. After any reply, Go deeper asks for a fuller one.'
    },
    {
        title: 'Check an answer',
        how: 'Under each reply, How I got this shows what the assistant read. Select any text in a reply to check it, explain it or rewrite it.'
    },
    {
        title: 'Learn the app',
        how: 'Ask how to do anything here, like adding a teammate, a program or a deliverable.',
        example: 'How do I add a teammate and give them access to a program?'
    }
];
/** "What can I do here?" under the new-chat composer, and the panel it opens. */ export function ChatCapabilities({ onTry }) {
    const [open, setOpen] = useState(false);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(SpecraButton, {
                size: "sm",
                variant: "ghost",
                "data-testid": Testids.assistant.capabilitiesButton,
                onClick: ()=>setOpen(true),
                children: [
                    /*#__PURE__*/ _jsx(CircleHelp, {
                        "aria-hidden": "true"
                    }),
                    "What can I do here?"
                ]
            }),
            /*#__PURE__*/ _jsx(Sheet, {
                open: open,
                onOpenChange: setOpen,
                children: /*#__PURE__*/ _jsxs(SheetContent, {
                    className: "flex w-full flex-col overflow-hidden sm:max-w-md",
                    "data-testid": Testids.assistant.capabilitiesPanel,
                    children: [
                        /*#__PURE__*/ _jsxs(SheetHeader, {
                            children: [
                                /*#__PURE__*/ _jsx(SheetTitle, {
                                    children: "What you can do in chat"
                                }),
                                /*#__PURE__*/ _jsx(SheetDescription, {
                                    children: "Try any of these. Nothing is sent until you press Send."
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "min-h-0 flex-1 space-y-4 overflow-y-auto pb-4",
                            children: CAPABILITIES.map((c)=>/*#__PURE__*/ _jsxs("section", {
                                    "aria-label": c.title,
                                    className: "space-y-1",
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraText, {
                                            as: "p",
                                            size: "body-ui",
                                            className: "font-semibold",
                                            children: c.title
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraText, {
                                            as: "p",
                                            size: "body-ui",
                                            tone: "muted",
                                            children: c.how
                                        }),
                                        c.example ? /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            variant: "secondary",
                                            "data-testid": Testids.assistant.capabilityTry,
                                            "data-capability": c.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
                                            onClick: ()=>{
                                                setOpen(false);
                                                onTry(c.example ?? '');
                                            },
                                            children: "Try it"
                                        }) : null
                                    ]
                                }, c.title))
                        })
                    ]
                })
            })
        ]
    });
}
